import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { DatePicker } from "@admin/components/molecules/Picker/DatePicker";
import {
  addFoodPoisoning,
  dateToISO,
  FoodPoisoning,
  updateFoodPoisoning,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface FoodPoisoningModalProps extends ModalProps {
  title: string;
  companyId?: string;
  companyLocationId?: string;
  initialData?: FoodPoisoning;
}

const foodPoisoningSchema = z.object({
  poisoningDate: z.string().min(1),
  poisoningDish: z.string().min(1),
  numberConsumed: z.string().min(1),
  sickAmount: z.string().min(1),
  consumptionDate: z.string().min(1),
  symptomsDate: z.string().min(1),
  leftOvers: z.string().min(1),
  visitedDoctor: z.string().optional(),
  contactedAuthority: z.string().optional(),
  note: z.string().optional(),
  symptoms: z.string().optional(),
});

type FoodPoisoningSchema = z.infer<typeof foodPoisoningSchema>;

export function FoodPoisoningModal({
  title,
  companyId,
  companyLocationId,
  initialData,
  ...props
}: FoodPoisoningModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: updateFoodPoisoning,
  });

  const mutation = useMutation({
    mutationFn: addFoodPoisoning,
  });

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<FoodPoisoningSchema>({
    resolver: zodResolver(foodPoisoningSchema),
  });
  const handleSubmitClick = handleSubmit((data) => {
    if (!companyId || !companyLocationId || !user?.id) return;

    if (initialData) {
      updateMutation.mutate(
        {
          ...data,
          id: initialData.id,
          companyId: companyId,
          companyLocationId: companyLocationId,
          registratedUser: user?.id,
          consumptionDate: dateToISO(new Date(data.consumptionDate)),
          symptomsDate: dateToISO(new Date(data.symptomsDate)),
          file: "",
        },
        {
          onSuccess: () => {
            toast.success(t("food.Food Poisoning Entry Added"));
            queryClient.invalidateQueries({
              queryKey: ["locationFoodPoisonings", companyLocationId],
            });
            reset();
            props?.onClose?.();
          },
          onError: () => {
            toast.error(t("food.Failed to add food poisoning entry"));
          },
        },
      );
      return;
    }

    mutation.mutate(
      {
        ...data,
        companyId: companyId,
        companyLocationId: companyLocationId,
        registratedUser: user?.id,
        consumptionDate: dateToISO(new Date(data.consumptionDate)),
        symptomsDate: dateToISO(new Date(data.symptomsDate)),
        file: "",
      },
      {
        onSuccess: () => {
          toast.success(t("food.Food Poisoning Entry Added"));
          reset();
          props?.onClose?.();
        },
        onError: () => {
          toast.error(t("food.Failed to add food poisoning entry"));
        },
      },
    );
  });

  const handleClose = () => {
    reset();
    props.onClose?.();
  };

  useEffect(() => {
    if (initialData) {
      reset({
        poisoningDate: initialData.poisoningDate,
        poisoningDish: initialData.poisoningDish,
        sickAmount: initialData.sickAmount,
        numberConsumed: initialData.numberConsumed,
        consumptionDate: initialData.consumptionDate,
        leftOvers: initialData.leftOvers,
        visitedDoctor: initialData.visitedDoctor,
        contactedAuthority: initialData.contactedAuthority,
        note: initialData.note,
        symptoms: initialData.symptoms,
        symptomsDate: initialData.symptomsDate,
      });
    }
  }, [initialData, reset]);

  return (
    <Modal
      title={title}
      {...props}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmitClick} loading={mutation.isPending}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
    >
      <form>
        <Label required title={t("food.What food was consumed")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("poisoningDish")}
              type="text"
              placeholder="-"
              name="poisoningDish"
            />
            <ErrorMessage message={errors?.poisoningDish?.message} />
          </div>
        </Label>
        <Label required title={t("food.Are there any food residues")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Select
              {...register("leftOvers")}
              variant="standart"
              options={[
                { label: t("common.Yes"), value: t("common.Yes") },
                { label: t("common.No"), value: t("common.No") },
              ]}
              defaultValue={initialData?.leftOvers}
              placeholder={t("common.Select an option")}
              name="leftOvers"
            />
            <ErrorMessage message={errors?.leftOvers?.message} />
          </div>
        </Label>
        <Label required title={t("food.How many people consumed")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("numberConsumed")}
              type="number"
              placeholder={t("food.Quantity Consumed")}
              name="numberConsumed"
            />
            <ErrorMessage message={errors?.numberConsumed?.message} />
          </div>
        </Label>
        <Label required title={t("food.Number of patients")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("sickAmount")}
              type="number"
              placeholder={t("food.Quantity patients")}
              name="sickAmount"
            />
            <ErrorMessage message={errors?.sickAmount?.message} />
          </div>
        </Label>
        <Label required title={t("food.Date and Time of Poisoning")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Controller
              name="poisoningDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? new Date(field.value) : new Date()}
                  onChange={(date: Date) => field.onChange(dateToISO(date))}
                />
              )}
            />
            <ErrorMessage message={errors?.poisoningDate?.message} />
          </div>
        </Label>

        <Label required title={t("food.Date and Time of Consumption")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Controller
              name="consumptionDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? new Date(field.value) : new Date()}
                  onChange={(date: Date) => field.onChange(dateToISO(date))}
                />
              )}
            />
            <ErrorMessage message={errors?.consumptionDate?.message} />
          </div>
        </Label>
        <Label required title={t("food.Date and Time of Symptoms")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Controller
              name="symptomsDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? new Date(field.value) : new Date()}
                  onChange={(date: Date) => field.onChange(dateToISO(date))}
                />
              )}
            />
            <ErrorMessage message={errors?.symptomsDate?.message} />
          </div>
        </Label>
        <Label title={t("food.Symptoms")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("symptoms")}
              type="text"
              placeholder={t("food.Symptoms")}
              name="symptoms"
            />
            <ErrorMessage message={errors?.symptoms?.message} />
          </div>
        </Label>
        <Label title={t("food.Doctor Appointment")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("visitedDoctor")}
              type="text"
              placeholder="-"
              name="visitedDoctor"
            />
            <ErrorMessage message={errors?.visitedDoctor?.message} />
          </div>
        </Label>
        <Label title={t("food.Contacted the food authority")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("contactedAuthority")}
              type="text"
              placeholder="-"
              name="contactedAuthority"
            />
            <ErrorMessage message={errors?.contactedAuthority?.message} />
          </div>
        </Label>
        <Label title={t("food.Remarks")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("note")}
              type="text"
              placeholder="-"
              name="note"
            />
            <ErrorMessage message={errors?.note?.message} />
          </div>
        </Label>
      </form>
    </Modal>
  );
}
